/**
 * Google Meet Enhanced Experience — video effects / recording for Meet
 *
 * A single WAR entry exposes `dependencies/*`, `img/*`, `assets/*`, `content.js` and
 * `main.css` to every origin with no use_dynamic_url, so all of its bundled effect icons
 * and scripts are fetchable from any page at a fixed URL. The `img/icons` effect thumbnails
 * are images, so they load through probeImage with no CORS grant and survive a strict page
 * CSP.
 *
 * Manifest verified against version 4.0.0.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'hodiladlefdpcbemnbbcpclbmknkiaem';

export const googleMeetEnhancedExperience: ExtensionDefinition = {
  id: ID,
  name: 'Google Meet Enhanced Experience',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 800_000,
  notes:
    'img/*, dependencies/*, content.js and main.css exposed to every origin with no ' +
    'dynamic URL. Effect-icon images load without a CORS grant and are conclusive.',
  signals: [
    {
      id: 'war-icon-blur',
      kind: 'web-accessible-resource',
      description:
        'Load img/icons/blur.png via <img> — an effect thumbnail exposed to every origin; ' +
        'needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'img/icons/blur.png'),
    },
    {
      id: 'war-icon-bokeh',
      kind: 'web-accessible-resource',
      description:
        'Load img/icons/bokeh.png via <img> — a second effect thumbnail, corroborating the ' +
        'identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'img/icons/bokeh.png'),
    },
    {
      id: 'war-loop-detector-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch dependencies/LoopDetector.js — a vendor bundled script, independent of the ' +
        'image paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'dependencies/LoopDetector.js'),
    },
  ],
};
